import React, { useRef } from "react";
import { Box, Card, Grid, LinearProgress, Typography } from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { styled } from "@mui/system";
import TitleBar from "components/TitleBar";
import { enterpriseTokens, overviewChartPalette } from "styles/theme";

const RoundedLinearProgress = styled(LinearProgress)(({ theme, barcolor }) => ({
    width: "100%",
    height: 16,
    borderTopRightRadius: "5px",
    borderBottomRightRadius: "5px",
    [`& .MuiLinearProgress-bar`]: {
        borderRadius: 0,
        backgroundColor: barcolor,
    },
}));

const ScheduleOverview = ({ kpiData, scheduleKpi, isLoading, error }) => {
    const chartRef = useRef(null);
    const apiData = scheduleKpi?.data?.[0]
    const totalMilestones = apiData?.totalMilestonesCount ?? 0;
    const completedlMilestones = apiData?.achievedMilestonesCount ?? 0;
    const missedMilestones = apiData?.missedMilestonesCount ?? 0;
    const upcomingMilestones = Math.max(totalMilestones - completedlMilestones - missedMilestones, 0);

    const chartOptions = {
        chart: {
            type: "bar",
            height: 220,
            backgroundColor: "transparent",
            margin: [28, 24, 58, 24],
        },
        title: {
            text: `${completedlMilestones} of ${totalMilestones} achieved`,
            align: "left",
            x: 16,
            style: {
                color: enterpriseTokens.navy,
                fontSize: "15px",
                fontWeight: "700",
            },
        },
        xAxis: {
            categories: ["Milestones"],
            visible: false,
        },
        yAxis: {
            min: 0,
            max: Math.max(totalMilestones, 1),
            gridLineWidth: 0,
            lineWidth: 0,
            tickWidth: 0,
            title: { text: null },
            labels: {
                enabled: false,
            },
        },
        legend: {
            enabled: true,
            align: "center",
            verticalAlign: "bottom",
            itemDistance: 22,
            symbolRadius: 0,
            symbolHeight: 9,
            symbolWidth: 9,
            itemStyle: { color: enterpriseTokens.slate, fontSize: "11px", fontWeight: "600" },
        },
        plotOptions: {
            series: {
                stacking: "normal",
                borderWidth: 0,
                borderRadius: 0,
                pointWidth: 42,
                dataLabels: {
                    enabled: true,
                    formatter: function () { return this.y > 0 ? this.y : ""; },
                    style: { color: "#FFFFFF", fontSize: "13px", fontWeight: "700", textOutline: "none" },
                },
            },
        },
        tooltip: {
            pointFormat: '<span style="color:{series.color}">●</span> {series.name}: <b>{point.y}</b><br/>',
        },
        credits: {
            enabled: false,
        },
        series: [
            { name: "Overdue", data: [missedMilestones], color: overviewChartPalette.coral },
            { name: "Upcoming", data: [upcomingMilestones], color: overviewChartPalette.amber },
            { name: "Achieved", data: [completedlMilestones], color: overviewChartPalette.teal },
        ],
    };


    return (

        <Card
            elevation={0}
            sx={{
                borderRadius: 3,
                border: `1px solid ${enterpriseTokens.lineSoft}`,
                width: '100%',
                pb: 2,
                height: "100%",
                boxSizing: "border-box",
                backgroundColor: "#FFFFFF",
            }}
        >
            <TitleBar title={"Schedule Overview"} description={"Track schedule efficiency in real time."} />
            {!isLoading && !error && apiData && <Grid container alignItems={"center"}>
                <Grid size={{ xs: 12, md: 5 }}>
                    {/* <HighchartsReact
                        highcharts={Highcharts}
                        options={options}
                    /> */}

                    <Box sx={{ flex: 1, p: 1.5, display: "grid", gap: 2 }}>

                        <Box sx={{ display: "grid", gridTemplateColumns: "180px minmax(0, 1fr)", alignItems: "center", gap: 1.5 }}>
                            <Typography
                                sx={{
                                    fontSize: "13px",
                                    fontWeight: 400,
                                    color: overviewChartPalette.indigo,
                                    fontFamily: "Outfit, sans-serif",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                Planned Completion
                            </Typography>

                            <Box sx={{ position: "relative", width: "100%", minWidth: 0 }}>
                                <RoundedLinearProgress
                                    variant="determinate"
                                    value={Math.min(Number(apiData?.planProgress) || 0, 100)}
                                    barcolor={overviewChartPalette.indigo}
                                />

                                <Typography
                                    sx={{
                                        position: "absolute",
                                        right: 8,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "#FFFFFF",
                                        fontWeight: 600,
                                        fontSize: "12px",
                                        lineHeight: 1,
                                    }}
                                >
                                    {apiData?.planProgress}%
                                </Typography>
                            </Box>
                        </Box>


                        <Box sx={{ display: "grid", gridTemplateColumns: "180px minmax(0, 1fr)", alignItems: "center", gap: 1.5 }}>
                            <Typography
                                sx={{
                                    fontSize: "13px",
                                    fontWeight: 400,
                                    color: overviewChartPalette.teal,
                                    fontFamily: "Outfit, sans-serif",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                Delivered Completion
                            </Typography>

                            <Box sx={{ position: "relative", width: "100%", minWidth: 0 }}>
                                <RoundedLinearProgress
                                    variant="determinate"
                                    value={Math.min(Number(apiData?.actualProgress) || 0, 100)}
                                    barcolor={overviewChartPalette.teal}
                                />

                                <Typography
                                    sx={{
                                        position: "absolute",
                                        right: 8,
                                        top: "50%",
                                        transform: "translateY(-50%)",
                                        color: "#FFFFFF",
                                        fontWeight: 600,
                                        fontSize: "12px",
                                        lineHeight: 1,
                                    }}
                                >
                                    {apiData?.actualProgress}%
                                </Typography>
                            </Box>
                        </Box>

                    </Box>

                    <Box sx={{
                        border: `1px solid ${enterpriseTokens.lineSoft}`,
                        borderLeft: `4px solid ${enterpriseTokens.primary}`,
                        borderRadius: "3px",
                        backgroundColor: "#F8FAFC",
                        p: 2,
                        ml: 2,
                        mt: "30px"
                    }}>
                        <Typography sx={{
                            fontFamily: "Inter, Outfit, sans-serif",
                            fontWeight: 850,
                            fontSize: '24px',
                            letterSpacing: "0px",
                            color: enterpriseTokens.navy
                        }}> {totalMilestones} <Typography sx={{ fontSize: "12px" }} component={"span"}> Total Milestones</Typography></Typography>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography sx={{
                                fontFamily: "Inter, Outfit, sans-serif",
                                fontWeight: 850,
                                fontSize: '24px',
                                letterSpacing: "0px",
                                color: '#1A932E'
                            }}>{completedlMilestones}
                                <Typography sx={{ fontSize: "12px" }} component={"span"}> Achieved</Typography>
                            </Typography>
                            <Typography sx={{
                                fontFamily: "Inter, Outfit, sans-serif",
                                fontWeight: 850,
                                fontSize: '24px',
                                letterSpacing: "0px",
                                color: '#FF383C'
                            }}>{missedMilestones}
                                <Typography sx={{ fontSize: "12px" }} component={"span"}> Overdue</Typography>
                            </Typography>
                        </Box>
                    </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 7 }}>
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
                    <Typography sx={{
                        fontFamily: "Inter, Outfit, sans-serif",
                        fontWeight: 800,
                        fontSize: '12px',
                        letterSpacing: "0px",
                        color: enterpriseTokens.muted,
                        textAlign: "center"
                    }}>Milestone Health</Typography>
                </Grid>
            </Grid>}
            {isLoading && (
                <Grid item sx={{ flex: "0 0 auto", display: 'flex', alignItems: 'center', px: 3 }}>
                    <Box>Loading Data...</Box>
                </Grid>
            )}
            {error && (
                <Grid item xs={12} sx={{ height: "100%", p: 2 }}>
                    <Box>
                        Error loading data: {error?.status} - {error?.data?.message || "Please try again."}
                    </Box>
                </Grid>
            )}
        </Card>
    );
}

export default ScheduleOverview
